/* ============================================================
   DW Arcade — DETAIL MAN: BEAD RUN (Sonic "Blue Spheres"-style special stage).
   Detail Man auto-runs across a checkerboard ball. BUFF the blue water beads for
   points; DODGE (or JUMP) the red grime/tar. RING GIMMICK: trace a loop AROUND a
   patch of beads (go around, don't plow through) and the enclosed beads convert to
   RINGS for a big bonus. Endless ramp, one life, score → tiered prize.

   READABILITY (the whole point — Sonic stays legible on a near-FLAT grid):
   · The playfield is flat + rectilinear up close; only the FAR horizon curves
     (the planet's limb). A strong dome up close made cell positions unreadable.
   · A green LANE road is painted down the column the runner is on, and the next
     couple of cells it will enter are outlined — so "is that grime in my path?"
     is obvious and deaths feel fair.
   · Beads/grime are PRE-RENDERED 3D-shaded sphere sprites with a hard contact
     shadow, so they read as rounded objects sitting ON their cell.

   This REPLACES the old top-down maze. File + registry key stay `pacman` on
   purpose (cabinet name/art, sprite manifest, liquid script tag + prize key all
   resolve off it). Rendering is a RASTER globe on a 2D canvas — no WebGL.

   The player is the "Detail Man" walk GIF (DwArcadeAssets.detailman.north, running
   away), a DOM <img> overlaid bottom-center, ALWAYS forward (no turn-lean), with a
   speed-synced stride bob. Vector fallback while it loads / if missing.

   GAME-MODULE CONTRACT (unchanged):
     window.DwArcadeGames.pacman(opts) -> {
       canvas, spriteLayer, width, height, controls,
       start(), stop(), reset(), setDir(dir), press(btn),
       update(dtSeconds), drawAttract(title), getScore(), getLives(), getState()
     }
   opts: { dev, aspect, onEnd(result), onScore(score), onState(state) }
   ============================================================ */
(function () {
  'use strict';

  var C = {
    sky1: '#1F1238', sky2: '#3A2563', star: '#9A8FBE',
    tileA: '#3A2563', tileB: '#1C1138', tileLine: '#52407E',     // checkerboard (brand purple) + clearer grid line
    boxFill: 'rgba(255,255,255,0.035)', boxLine: 'rgba(231,236,250,0.22)', // the runner's OWN tile — subtle (just enough to read "you are here")
    limb: '#14102A',
    text: '#F7F8FA', dim: '#9A8FBE', gold: '#D4A437',
    green: '#22B84A', urgent: '#E63946', band: '#05030c', shadow: 'rgba(0,0,0,0.45)', grimeGlow: '#E63946'
  };

  var HALFPI = Math.PI / 2;
  var DIRV = [{ x: 0, y: -1 }, { x: 1, y: 0 }, { x: 0, y: 1 }, { x: -1, y: 0 }]; // 0=up 1=right 2=down 3=left
  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
  function hash(x, y) { var h = (x | 0) * 374761393 + (y | 0) * 668265263; h = (h ^ (h >> 13)) * 1274126177; return ((h ^ (h >> 16)) >>> 0) / 4294967296; }

  // Pre-rendered 3D-shaded sphere → drawImage scaled per cell (fast + consistent at any size).
  function makeSphere(light, base, dark, rimCol) {
    var S = 128, cv = document.createElement('canvas'); cv.width = cv.height = S;
    var c = cv.getContext('2d'), cx = S / 2, cy = S / 2, r = S / 2 - 5;
    var g = c.createRadialGradient(cx - r * 0.36, cy - r * 0.42, r * 0.08, cx, cy, r);
    g.addColorStop(0, light); g.addColorStop(0.5, base); g.addColorStop(1, dark);
    c.fillStyle = g; c.beginPath(); c.arc(cx, cy, r, 0, 7); c.fill();
    if (rimCol) { c.strokeStyle = rimCol; c.lineWidth = r * 0.1; c.beginPath(); c.arc(cx, cy, r - r * 0.06, 0.12 * Math.PI, 0.88 * Math.PI); c.stroke(); } // bottom rim light
    c.fillStyle = 'rgba(255,255,255,0.92)'; c.beginPath(); c.arc(cx - r * 0.32, cy - r * 0.36, r * 0.17, 0, 7); c.fill();   // specular
    return cv;
  }

  function create(opts) {
    opts = opts || {};
    var dev = !!opts.dev;
    var onEnd = opts.onEnd || function () {};
    var onScore = opts.onScore || function () {};
    var onState = opts.onState || function () {};

    // ---- Canvas ----
    var aspect = clamp((opts.aspect && opts.aspect > 0) ? opts.aspect : 0.62, 0.5, 0.8);
    var W = 460, H = clamp(Math.round(W / aspect), Math.round(W * 1.28), Math.round(W * 1.95));
    var CX = W / 2;
    var canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H;
    var ctx = canvas.getContext('2d');

    // ---- Projection knobs (visual-tune; eyeball in the cabinet) ----
    // Near field is FLAT + readable; only the far horizon bows (BOW gated by depth).
    var FOCAL = W * 0.64, CAM_BACK = 2.2, CAM_H = 1.95;
    var HORIZON = H * 0.36, NEAR = 0.9, BOW = H * 0.20;
    var VIEW_R = 13;
    var FOOT_Y = H * 0.78, PLAYER_W = W * 0.36, JUMP_LIFT = H * 0.12;   // sprite anchored by its FEET at FOOT_Y
    var FOOT_PAD = 0.13;                                                // drop the sprite this fraction of its height to eat the GIF's transparent base → feet plant on the shadow
    var BEAD_R = 0.20, GRIME_R = 0.23, RING_R = 0.22, RMAX = W * 0.14;  // smaller → each sphere sits clearly inside one tile

    // ---- Pace (calmer base + gentler ramp so the field stays readable) ----
    var BASE_SPEED = 2.6, MAX_SPEED = 5.4, SPEED_K = 0.008;
    var STRIDE_RATE = 2.4;                                    // stride bob frequency per unit speed
    var TURN_DUR = 0.14, JUMP_DUR = 0.40, JUMP_CD = 0;       // TURN_DUR = the world-FREEZE while the field rotates 90° (Blue Spheres) · no jump cooldown
    var BEAD_DENSITY = 0.34, RING_VALUE = 60;
    var MULT_CAP = 4, STREAK_RESET = 3, DEMO_MAX = 25;
    function speedNow() { return clamp(BASE_SPEED + dist * SPEED_K, BASE_SPEED, MAX_SPEED); }
    function grimeDensity() { return clamp(0.04 + dist * 0.0008, 0.04, 0.15); }

    var beadSprite = makeSphere('#D6F0FF', '#2FA8FF', '#0C3B86', '#6FCBFF');
    var grimeSprite = makeSphere('#454B57', '#1B1E25', '#070809', '#E63946');

    // ---- Detail Man sprite (DOM <img> overlay, north, always forward) ----
    var DM = (window.DwArcadeAssets && window.DwArcadeAssets.detailman) || null;
    var dmSrc = (DM && DM.north) ? { north: DM.north } : null;
    var spriteLayer = null, playerImg = null;
    if (dmSrc) {
      var pre = new Image(); pre.src = dmSrc.north;
      spriteLayer = document.createElement('div'); spriteLayer.className = 'dw-dm-layer';
      playerImg = document.createElement('img'); playerImg.className = 'dw-dm-player'; playerImg.alt = '';
      playerImg.src = dmSrc.north; playerImg._src = dmSrc.north;
      spriteLayer.appendChild(playerImg);
    }
    function imgReady(im) { return !!(im && im.complete && im.naturalWidth > 0); }

    // ---- State ----
    var cval;                                  // "gx,gy" -> 'empty'|'bead'|'grime'
    var cellX, cellY, dirIdx, prog, headA, targA;   // grid cell + progress (0..1) to the next center + heading
    var turnBuf, turning, turnT, turnFrom;          // buffered turn + the freeze-rotation state
    var jumpT, jumpCd, score, dist, streak, cellsSinceBead, parts, banner, bannerT, nextMile, stride;
    var state, frame, demo, demoClock, endT, trail;

    function setState(s) { if (state !== s) { state = s; onState(s); } }
    function airborne() { return jumpT > 0; }

    function content(gx, gy) {
      var k = gx + ',' + gy;
      var v = cval[k]; if (v !== undefined) return v;
      var t = 'empty';
      if (Math.max(Math.abs(gx), Math.abs(gy)) > 3) {
        if (hash(gx, gy) < grimeDensity()) t = 'grime';
        else if (hash(gx + 101, gy - 57) < BEAD_DENSITY) t = 'bead';
      } else if (!(gx === 0 && gy === 0) && hash(gx + 101, gy - 57) < 0.45) { t = 'bead'; }
      cval[k] = t; return t;
    }
    function pruneFar() {
      var cxi = cellX, cyi = cellY;
      for (var k in cval) {
        var c = k.indexOf(','), gx = +k.slice(0, c), gy = +k.slice(c + 1);
        if (Math.max(Math.abs(gx - cxi), Math.abs(gy - cyi)) > VIEW_R + 4) delete cval[k];
      }
    }

    function reset() {
      cval = {};
      cellX = 0; cellY = 0; dirIdx = 0; prog = 0; headA = 0; targA = 0;
      turnBuf = 0; turning = false; turnT = 0; turnFrom = 0;
      jumpT = 0; jumpCd = 0; score = 0; dist = 0; streak = 0; cellsSinceBead = 0; stride = 0;
      parts = []; banner = ''; bannerT = 0; nextMile = 45; frame = 0; endT = 0;
      trail = [{ x: 0, y: 0 }];
      setState('attract'); onScore(0); render();
    }

    function start() { demo = false; reset(); setState('playing'); }
    function stop() { demo = false; setState('attract'); }
    function demoStart() { demo = true; reset(); demoClock = 0; setState('playing'); }

    // ---- Input: L/R = relative 90° turn; up/down ignored; any action = jump ----
    // A turn BUFFERS a 90° pivot that executes at the next grid intersection, where the world FREEZES and
    // rotates (Blue Spheres-style). Buffering = the press is never dropped; the freeze = you can't slide
    // forward into a hazard mid-turn. Together that's what makes turning feel fair.
    function setDir(d) {
      if (demo || state !== 'playing') return;
      if (d === 'left') turnBuf = -1; else if (d === 'right') turnBuf = 1;
    }
    function startTurn(t) {
      dirIdx = (dirIdx + t + 4) % 4;
      turnFrom = headA; targA += t * HALFPI;                 // rotate the field 90° over TURN_DUR while frozen
      turning = true; turnT = TURN_DUR; turnBuf = 0; prog = 0;   // stop dead at the intersection
    }
    function press(b) { if (demo || state !== 'playing') return; if (b === 'jump' || b === 'a' || b === 'b') doJump(); }
    function doJump() { if (jumpT <= 0 && jumpCd <= 0) jumpT = JUMP_DUR; }

    // ---- Cell resolution (runs as the runner reaches each cell center) ----
    function enterCell(cx, cy) {
      dist++;
      var t = content(cx, cy);
      if (t === 'bead') collect(cx, cy);
      else if (t === 'grime') { if (!airborne()) { crash(); return; } }   // jump = pass over, leave it
      score += 1;
      cellsSinceBead++; if (cellsSinceBead > STREAK_RESET) streak = 0;
      registerTrail(cx, cy);
      if (dist >= nextMile) milestone();
      if (demo) { var turn = demoChoose(cx, cy); if (turn) turnBuf = turn; }   // demo also turns at intersections (with the freeze)
    }
    function multNow() { return 1 + Math.min(MULT_CAP - 1, Math.floor(streak / 8)); }
    function collect(gx, gy) {
      cval[gx + ',' + gy] = 'empty';
      streak++; cellsSinceBead = 0; score += 25 * multNow(); pop(gx, gy, '#6FCBFF', 7);
      onScore(score);
    }
    function pop(gx, gy, col, n) { for (var i = 0; i < n; i++) parts.push({ gx: gx, gy: gy, vx: (Math.random() - 0.5) * 3, vy: (Math.random() - 0.5) * 3, vz: Math.random() * 2 + 0.5, a: 1, col: col }); }
    function milestone() { nextMile += Math.round(55 + dist * 0.25); banner = 'FASTER!'; bannerT = 1.1; }
    function crash() {
      setState('over'); endT = 0;
      for (var i = 0; i < 22; i++) parts.push({ gx: pwx(), gy: pwy(), vx: (Math.random() - 0.5) * 5, vy: (Math.random() - 0.5) * 5, vz: Math.random() * 3 + 1, a: 1, col: i % 2 ? C.grimeGlow : C.text });
      if (!demo) onEnd({ score: score, won: dist >= 220, lives: 0 });
    }

    // ---- Ring gimmick: a loop in the run traces a perimeter; enclosed beads → rings ----
    function registerTrail(x, y) {
      for (var i = trail.length - 1; i >= 0; i--) {
        if (trail[i].x === x && trail[i].y === y) { if (trail.length - i >= 4) closeLoop(trail.slice(i)); trail.length = i; break; }
      }
      trail.push({ x: x, y: y });
      if (trail.length > 80) trail.shift();
    }
    function closeLoop(loop) {
      var minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity, wall = {};
      loop.forEach(function (c) { wall[c.x + ',' + c.y] = 1; if (c.x < minx) minx = c.x; if (c.x > maxx) maxx = c.x; if (c.y < miny) miny = c.y; if (c.y > maxy) maxy = c.y; });
      var x0 = minx - 1, y0 = miny - 1, x1 = maxx + 1, y1 = maxy + 1, out = {}, q = [[x0, y0]];
      out[x0 + ',' + y0] = 1;
      while (q.length) {
        var n = q.pop(), nb = [[n[0] + 1, n[1]], [n[0] - 1, n[1]], [n[0], n[1] + 1], [n[0], n[1] - 1]];
        for (var j = 0; j < 4; j++) {
          var nx = nb[j][0], ny = nb[j][1], k = nx + ',' + ny;
          if (nx < x0 || nx > x1 || ny < y0 || ny > y1 || out[k] || wall[k]) continue;
          out[k] = 1; q.push([nx, ny]);
        }
      }
      var got = 0;
      for (var gx = minx; gx <= maxx; gx++) for (var gy = miny; gy <= maxy; gy++) {
        var key = gx + ',' + gy;
        if (wall[key] || out[key]) continue;
        if (content(gx, gy) === 'bead') { cval[key] = 'empty'; parts.push({ gx: gx, gy: gy, vx: 0, vy: 0, vz: 0, a: 1, ring: true, col: C.gold }); got++; }
      }
      if (got > 0) { var bonus = got * RING_VALUE + got * got * 6; score += bonus; onScore(score); banner = 'RING BONUS  +' + bonus; bannerT = 1.5; }
    }

    // ---- Demo AI ----
    function cellVal(cx, cy, idx) { var d = DIRV[idx], t = content(cx + d.x, cy + d.y); return t === 'grime' ? -100 : t === 'bead' ? 12 : 2; }
    function demoChoose(cx, cy) {
      var f = cellVal(cx, cy, dirIdx), l = cellVal(cx, cy, (dirIdx + 3) % 4), r = cellVal(cx, cy, (dirIdx + 1) % 4), best = f, turn = 0;
      if (l > best) { best = l; turn = -1; }
      if (r > best) { best = r; turn = 1; }
      if (best <= -100) { doJump(); turn = 0; }
      else if (f === best && f > 0 && (frame % 47) === 0) turn = 0;
      return turn;
    }

    // ---- Update ----
    function update(dt) {
      frame++;
      dt = Math.min(dt, 0.05);
      for (var i = parts.length - 1; i >= 0; i--) { var p = parts[i]; p.gx += p.vx * dt; p.gy += p.vy * dt; p.vz -= 6 * dt; p.a -= dt * (p.ring ? 1.5 : 1.4); if (p.a <= 0) parts.splice(i, 1); }
      if (jumpT > 0) { jumpT -= dt; if (jumpT <= 0) { jumpT = 0; jumpCd = JUMP_CD; } } else if (jumpCd > 0) jumpCd -= dt;
      if (bannerT > 0) bannerT -= dt;

      if (state === 'over') { endT += dt; if (demo && endT > 1.4) demoStart(); render(); return demo; }
      if (state !== 'playing') { render(); return demo; }

      if (demo) { demoClock += dt; if (demoClock >= DEMO_MAX) { demoStart(); render(); return true; } }

      // FREEZE-TURN: while the field rotates 90°, the runner is stopped dead — it can't slide into a hazard
      // mid-turn. This is the Blue Spheres "the world freezes as you turn" feel = fair turning.
      if (turning) {
        turnT -= dt;
        var tk = clamp(1 - turnT / TURN_DUR, 0, 1);
        headA = turnFrom + (targA - turnFrom) * (tk * tk * (3 - 2 * tk));   // smoothstep the rotation
        if (turnT <= 0) { turning = false; headA = targA; }
        render(); return true;
      }

      var sp = speedNow();
      stride += sp * STRIDE_RATE * dt;                       // stride bob keeps pace with the world
      prog += sp * dt;
      var guard = 0;
      while (prog >= 1 && state === 'playing' && guard++ < 8) {
        prog -= 1;
        cellX += DIRV[dirIdx].x; cellY += DIRV[dirIdx].y;
        enterCell(cellX, cellY);
        if (state !== 'playing') break;
        if (turnBuf) { startTurn(turnBuf); break; }          // reached an intersection with a turn queued → freeze + rotate
      }
      if ((frame & 63) === 0) pruneFar();

      render();
      return true;
    }

    // ---- Projection ----
    function pwx() { return cellX + DIRV[dirIdx].x * prog; }
    function pwy() { return cellY + DIRV[dirIdx].y * prog; }
    function projWorld(wx, wy, ox, oy, sinA, cosA) {
      var dx = wx - ox, dy = wy - oy;
      var fwd = dx * sinA - dy * cosA;
      var side = dx * cosA + dy * sinA;
      var depth = fwd + CAM_BACK;
      if (depth < NEAR) return null;
      var inv = 1 / depth;
      var sx = CX + side * FOCAL * inv;
      var sy = HORIZON + CAM_H * FOCAL * inv;
      var off = (sx - CX) / (W * 0.5);
      var fade = clamp((depth - 3.0) * 0.14, 0, 1);           // curve ONLY the far horizon → near grid stays flat/readable
      sy += BOW * off * off * fade;
      return { x: sx, y: sy, depth: depth, scale: FOCAL * inv };
    }

    function render() {
      var ox = pwx(), oy = pwy(), sinA = Math.sin(headA), cosA = Math.cos(headA);

      // backdrop
      var g = ctx.createLinearGradient(0, 0, 0, HORIZON); g.addColorStop(0, C.sky1); g.addColorStop(1, C.sky2);
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, HORIZON + 2);
      ctx.fillStyle = C.star;
      for (var s = 0; s < 26; s++) { var stx = ((s * 97 + 13) % W), sty = ((s * 53 + 7) % Math.round(HORIZON - 8)); ctx.globalAlpha = 0.18 + (s % 5) * 0.06; ctx.fillRect(stx, sty, 2, 2); }
      ctx.globalAlpha = 1;
      ctx.fillStyle = C.limb; ctx.fillRect(0, HORIZON, W, 4);
      ctx.fillStyle = C.tileB; ctx.fillRect(0, HORIZON + 4, W, H - HORIZON - 4);

      // project the corner lattice → checker cells far→near
      var cxi = Math.round(ox), cyi = Math.round(oy);
      var ncs = 2 * VIEW_R + 2, baseX = cxi - VIEW_R - 0.5, baseY = cyi - VIEW_R - 0.5;
      var lat = new Array(ncs);
      for (var i = 0; i < ncs; i++) { lat[i] = new Array(ncs); for (var j = 0; j < ncs; j++) lat[i][j] = projWorld(baseX + i, baseY + j, ox, oy, sinA, cosA); }

      var cells = [];
      for (var a = 0; a < ncs - 1; a++) for (var b = 0; b < ncs - 1; b++) {
        var c0 = lat[a][b], c1 = lat[a + 1][b], c2 = lat[a + 1][b + 1], c3 = lat[a][b + 1];
        if (!c0 || !c1 || !c2 || !c3) continue;
        var minx = Math.min(c0.x, c1.x, c2.x, c3.x), maxx = Math.max(c0.x, c1.x, c2.x, c3.x);
        var miny = Math.min(c0.y, c1.y, c2.y, c3.y), maxy = Math.max(c0.y, c1.y, c2.y, c3.y);
        if (maxx < 0 || minx > W || maxy < HORIZON || miny > H) continue;
        cells.push({ gx: cxi - VIEW_R + a, gy: cyi - VIEW_R + b, c0: c0, c1: c1, c2: c2, c3: c3, depth: (c0.depth + c2.depth) * 0.5 });
      }
      cells.sort(function (p, q) { return q.depth - p.depth; });

      // pass 1: checker tiles + grid lines (the accurate floor)
      ctx.lineWidth = 1;
      cells.forEach(function (c) {
        ctx.beginPath(); ctx.moveTo(c.c0.x, c.c0.y); ctx.lineTo(c.c1.x, c.c1.y); ctx.lineTo(c.c2.x, c.c2.y); ctx.lineTo(c.c3.x, c.c3.y); ctx.closePath();
        ctx.fillStyle = ((c.gx + c.gy) & 1) ? C.tileA : C.tileB; ctx.fill();
        ctx.strokeStyle = C.tileLine; ctx.stroke();
      });
      // the runner's OWN tile — a stable square bounding box at the feet (where you are = your collision footprint)
      drawPlayerBox(ox, oy, sinA, cosA);
      // pass 2: spheres on top, far→near
      cells.forEach(function (c) { var t = content(c.gx, c.gy); if (t !== 'empty') drawObject(t, c.gx, c.gy, ox, oy, sinA, cosA); });

      // particles
      parts.forEach(function (p) {
        var pr = projWorld(p.gx, p.gy, ox, oy, sinA, cosA); if (!pr) return;
        ctx.globalAlpha = Math.max(0, p.a);
        if (p.ring) {
          var rr = Math.min((RING_R + (1 - p.a) * 0.16) * pr.scale, RMAX), lift = (1 - p.a) * 0.45 * pr.scale;
          ctx.strokeStyle = C.gold; ctx.lineWidth = Math.max(2, rr * 0.3);
          ctx.beginPath(); ctx.ellipse(pr.x, pr.y - rr - lift, rr, rr * 0.72, 0, 0, 7); ctx.stroke();
        } else { ctx.fillStyle = p.col; var r = Math.max(1.5, 0.07 * pr.scale); ctx.fillRect(pr.x - r, pr.y - p.vz * 6 - r, r * 2, r * 2); }
      });
      ctx.globalAlpha = 1;

      drawPlayer();
      drawHud();
      if (state === 'attract') midText('DETAIL MAN', '#2FA8FF', 'CLICK TO PLAY · BUFF THE BEADS');
      else if (state === 'over') midText('WIPED OUT', C.urgent, 'SCORE ' + score);
      else if (bannerT > 0 && banner) midText(banner, banner.charAt(0) === 'R' ? C.gold : C.green, '');

      if (dmSrc) updateSprite();
    }

    function drawObject(t, gx, gy, ox, oy, sinA, cosA) {
      var pr = projWorld(gx, gy, ox, oy, sinA, cosA); if (!pr || pr.scale < 5) return;
      var x = pr.x, sc = pr.scale, isG = t === 'grime';
      var r = Math.min((isG ? GRIME_R : BEAD_R) * sc, RMAX), y = pr.y - r;
      if (isG) { ctx.fillStyle = C.grimeGlow; ctx.globalAlpha = 0.22; ctx.beginPath(); ctx.ellipse(x, pr.y, r * 1.2, r * 0.42, 0, 0, 7); ctx.fill(); ctx.globalAlpha = 1; }
      ctx.fillStyle = C.shadow; ctx.beginPath(); ctx.ellipse(x, pr.y, r * 0.98, r * 0.34, 0, 0, 7); ctx.fill();  // hard contact shadow anchors it to the cell
      ctx.drawImage(isG ? grimeSprite : beadSprite, x - r, y - r, r * 2, r * 2);
    }

    // the runner's collision tile, projected as a square right under the character — stable on screen, grid-accurate
    function drawPlayerBox(ox, oy, sinA, cosA) {
      function corner(f, s) { return projWorld(ox + f * sinA + s * cosA, oy - f * cosA + s * sinA, ox, oy, sinA, cosA); }
      var p0 = corner(0.46, -0.46), p1 = corner(0.46, 0.46), p2 = corner(-0.46, 0.46), p3 = corner(-0.46, -0.46);
      if (!p0 || !p1 || !p2 || !p3) return;
      ctx.beginPath(); ctx.moveTo(p0.x, p0.y); ctx.lineTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.lineTo(p3.x, p3.y); ctx.closePath();
      ctx.fillStyle = C.boxFill; ctx.fill();
      ctx.strokeStyle = C.boxLine; ctx.lineWidth = 1.3; ctx.stroke(); ctx.lineWidth = 1;
    }

    function jumpLiftPx() { if (jumpT <= 0) return 0; var prg = 1 - jumpT / JUMP_DUR; return Math.sin(Math.PI * prg) * JUMP_LIFT; }
    function strideBob() { return (state === 'playing' && jumpT <= 0) ? Math.abs(Math.sin(stride)) * H * 0.004 : 0; }

    function drawPlayer() {
      var lift = jumpLiftPx(), bob = strideBob();
      var ssc = 1 - (lift / JUMP_LIFT) * 0.5;
      // strong grounded shadow at the FEET (rises/shrinks with the jump)
      ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.beginPath(); ctx.ellipse(CX, FOOT_Y - lift * 0.12, W * 0.115 * ssc, H * 0.024 * ssc, 0, 0, 7); ctx.fill();
      if (!dmSrc || !imgReady(playerImg)) {
        if (state === 'over') return;
        var py = FOOT_Y - lift - bob, bw = W * 0.10, bh = H * 0.09;       // anchored by feet
        ctx.fillStyle = C.green; ctx.fillRect(CX - bw / 2, py - bh, bw, bh);
        ctx.fillStyle = C.text; ctx.fillRect(CX - bw / 2, py - bh, bw, bh * 0.28);
        ctx.fillStyle = '#1F1238'; ctx.fillRect(CX - bw * 0.28, py - bh - bh * 0.4, bw * 0.56, bh * 0.4);
      }
    }

    function updateSprite() {
      if (!playerImg || !spriteLayer.parentNode) return;
      if (state === 'over') { playerImg.style.opacity = '0'; return; }
      var cr = canvas.getBoundingClientRect(), lr = spriteLayer.getBoundingClientRect();
      if (!cr.width || !cr.height) return;
      var sxr = cr.width / W, syr = cr.height / H, lift = jumpLiftPx(), bob = strideBob();
      var aspectH = imgReady(playerImg) ? (playerImg.naturalHeight / playerImg.naturalWidth) : 1.3;
      var drop = PLAYER_W * aspectH * FOOT_PAD;                           // push down to plant the visible feet on the shadow (GIF has transparent base)
      var px = CX, py = FOOT_Y - lift - bob + drop;                       // anchor the sprite by its FEET, dropped past the padding
      if (playerImg._src !== dmSrc.north) { playerImg.src = dmSrc.north; playerImg._src = dmSrc.north; }
      var grow = 1 + (lift / JUMP_LIFT) * 0.16;
      playerImg.style.opacity = imgReady(playerImg) ? '1' : '0';
      playerImg.style.width = (PLAYER_W * sxr * grow) + 'px';
      playerImg.style.transform = 'translate(' + ((cr.left - lr.left) + px * sxr) + 'px,' + ((cr.top - lr.top) + py * syr) + 'px) translate(-50%,-100%)';
    }

    function drawHud() {
      var pad = Math.round(W * 0.04), y0 = Math.round(H * 0.02);
      ctx.textBaseline = 'top';
      ctx.fillStyle = C.dim; ctx.font = '700 ' + Math.round(W * 0.032) + 'px DM Sans, Arial'; ctx.textAlign = 'left';
      ctx.fillText('SCORE', pad, y0);
      ctx.fillStyle = C.text; ctx.font = '800 ' + Math.round(W * 0.06) + 'px Outfit, Arial';
      ctx.fillText(String(score), pad, y0 + Math.round(W * 0.035));
      var m = multNow();
      ctx.textAlign = 'right';
      ctx.fillStyle = m > 1 ? C.gold : C.dim; ctx.font = '800 ' + Math.round(W * 0.05) + 'px Outfit, Arial';
      ctx.fillText('x' + m, W - pad, y0 + Math.round(W * 0.035));
      ctx.fillStyle = C.dim; ctx.font = '700 ' + Math.round(W * 0.028) + 'px DM Sans, Arial';
      ctx.fillText(dist + 'm', W - pad, y0);
    }

    function midText(big, col, sub) {
      ctx.fillStyle = 'rgba(5,3,12,0.72)'; ctx.fillRect(0, H * 0.40, W, sub ? H * 0.20 : H * 0.13);
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = col; ctx.font = '800 ' + Math.round(W * 0.082) + 'px Outfit, Arial'; ctx.shadowColor = col; ctx.shadowBlur = 16;
      ctx.fillText(big, CX, H * (sub ? 0.47 : 0.465)); ctx.shadowBlur = 0;
      if (sub) { ctx.fillStyle = C.text; ctx.font = '700 ' + Math.round(W * 0.032) + 'px DM Sans, Arial'; ctx.fillText(sub, CX, H * 0.55); }
    }

    function drawAttract() { reset(); }
    reset();

    return {
      canvas: canvas, spriteLayer: spriteLayer, width: W, height: H,
      // touch:'turn-jump' — horizontal swipe turns at the next intersection, a tap
      // (or swipe up) jumps. Desktop uses ◀ ▶ / A D + Space. dpad/buttons kept for
      // legacy callers only; the engine no longer paints them.
      controls: { dpad: true, showLives: false, touch: 'turn-jump', buttons: [{ id: 'jump', label: 'JUMP ▲' }] },
      tutorial: [
        { m: { g: 'swipe', t: 'Swipe ◀ ▶ to turn at intersections' }, d: { k: ['◀', '▶'], t: 'Press ◀ ▶ (or A / D) to turn' } },
        { m: { g: 'tap', t: 'Tap to JUMP the red grime' }, d: { k: ['SPACE'], t: 'Press SPACE to JUMP the grime' } },
        { m: { g: 'tip', t: 'Buff blue beads · loop a patch for a RING bonus' }, d: { g: 'tip', t: 'Buff blue beads · loop a patch for a RING bonus' } }
      ],
      start: start, stop: stop, reset: reset, setDir: setDir, press: press, update: update,
      demo: demoStart, drawAttract: drawAttract,
      getScore: function () { return score; },
      getLives: function () { return 0; },
      getState: function () { return state; }
    };
  }

  window.DwArcadeGames = window.DwArcadeGames || {};
  window.DwArcadeGames.pacman = create;
})();
